import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

const COLLECTIONS = [
  { key: "Modern", label: "Modern House Plans", desc: "Sculpted volumes, floor-to-ceiling glass, editorial minimalism.", img: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { key: "Luxury Villa", label: "Luxury Villas", desc: "5,000+ sqft estates with pools, wine cellars, and guest wings.", img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=70" },
  { key: "Farmhouse", label: "Farmhouse Plans", desc: "Warm timber, wraparound porches, aging-in-place primary suites.", img: "https://images.pexels.com/photos/7031581/pexels-photo-7031581.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { key: "Duplex", label: "Duplex Plans", desc: "Mirror-image units for multi-generational living or rental income.", img: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1200&q=70" },
  { key: "Traditional", label: "Traditional Homes", desc: "Symmetric brick facades, classical proportions, formal rooms.", img: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=1200&q=70" },
  { key: "Small", label: "Small House Plans", desc: "Under 1,500 sqft — starter homes, cottages, and ADUs.", img: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1200&q=70" },
  { key: "Contemporary", label: "Contemporary Designs", desc: "Bold single-story L-plans, courtyards, and clerestory light.", img: "https://images.pexels.com/photos/7031622/pexels-photo-7031622.jpeg?auto=compress&cs=tinysrgb&w=1200" },
];

const PlanCollections = () => {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    document.title = "Plan Collections — Modern, Luxury, Farmhouse & More | Dream Home Engineers";
    api.get("/plan-counts").then((r) => setCounts(r.data.by_style || {})).catch(() => {});
  }, []);

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-24">
        <div className="max-w-3xl">
          <div className="overline text-accent">Plan Collections</div>
          <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95] tracking-tighter">
            Browse by style.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Seven carefully curated collections covering every residential typology — from 720 sqft studio ADUs to 5,800 sqft luxury villas.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {COLLECTIONS.map((c) => (
            <Link key={c.key} to={`/plans?style=${encodeURIComponent(c.key)}`} className="group block plan-card-hover border border-border" data-testid={`collection-${c.key}`}>
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img src={c.img} alt={`${c.label} collection`} className="plan-card-img w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-7 flex items-start justify-between gap-4">
                <div>
                  <div className="overline text-accent">{counts[c.key] ?? 0} plan{(counts[c.key] ?? 0) === 1 ? "" : "s"}</div>
                  <h2 className="font-display text-3xl mt-2 leading-tight group-hover:text-accent transition-fast">{c.label}</h2>
                  <p className="mt-3 text-sm text-muted-foreground">{c.desc}</p>
                </div>
                <ArrowRight size={20} className="shrink-0 mt-2 text-accent" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
};

export default PlanCollections;
